import React from 'react';
import { formatDate } from '../../utils/dateUtils';

/**
 * AdminPFNominationPreview - Read-only admin preview for PF Nomination form
 * 
 * Renders PF Nomination form data directly from submission.form_data
 * No routing, no API calls, no submit functionality.
 * Designed for Employee360 modal display.
 */
export default function AdminPFNominationPreview({ submission, readOnly = true }) {
  const formData = submission?.form_data || {};

  const currentDate = formatDate(new Date());

  return (
    <div className="bg-gray-50 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Document Container */}
        <div className="bg-white border border-gray-300 shadow-sm" style={{ minHeight: '1123px' }}>
          {/* Header */}
          <div className="text-center border-b-2 border-black py-6 px-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Pune Cantonment Board</h1>
            <p className="text-sm text-gray-700 mb-4">Golibar Maidan, Pune-1</p>
            <div className="inline-block border-2 border-black px-6 py-1 mb-2">
              <span className="font-bold text-lg">Form "A"</span>
            </div>
            <p className="text-sm font-semibold text-gray-800 mt-3">SCHEDULE II</p>
            <p className="text-sm text-gray-700 mt-1">Form of Nomination when depositor has a dependent or dependants.</p>
          </div>

          {/* Declaration Section */}
          <div className="px-8 py-6 border-b border-gray-300">
            <p className="text-sm text-gray-800 leading-relaxed text-justify">
              &nbsp;&nbsp;&nbsp;&nbsp; I hereby direct that the amount at my credit in the Provident Fund, in the event of my death, 
              shall be paid to the nominee(s) mentioned below in the proportion shown against their names. 
              I hereby certify that the person(s) mentioned below is/are dependent on me and that I have not 
              made any other nomination for the Provident Fund.
            </p>
          </div>

          {/* Depositor Information */}
          <div className="px-8 py-4 border-b border-gray-300">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-semibold text-sm text-gray-700">Name of Depositor:</span>
                <p className="text-sm text-gray-900 mt-1">{formData.depositorName || '________________________'}</p>
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-700">Address of Depositor:</span>
                <p className="text-sm text-gray-900 mt-1 whitespace-pre-line">{formData.depositorAddress || '________________________'}</p>
              </div>
            </div>
          </div>

          {/* Nominee Table */}
          <div className="px-8 py-6 border-b border-gray-300">
            <table className="w-full border-collapse border border-gray-400">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Name and Address of the Nominee or Nominees
                  </th>
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Relationship if any with the Depositor
                  </th>
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Age of the Nominee
                  </th>
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Amount of Share of Accumulations
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.nominees && formData.nominees.length > 0 ? (
                  formData.nominees.map((nominee, index) => (
                    <tr key={index}>
                      <td className="border border-gray-400 px-3 py-2 align-top">
                        <div className="text-sm text-gray-900">
                          <p className="font-medium">{nominee.name || '________________________'}</p>
                          <p className="mt-1 whitespace-pre-line text-xs">{nominee.address || '________________________'}</p>
                        </div>
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-sm text-gray-900 align-top">
                        {nominee.relationship || '________________________'}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-sm text-gray-900 align-top">
                        {nominee.age || '____'}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-sm text-gray-900 align-top">
                        {nominee.sharePercentage ? `${nominee.sharePercentage}%` : '____%'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 text-sm text-gray-400" colSpan="4">
                      No nominees added
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Signature Section */}
          <div className="px-8 py-6">
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <span className="text-sm text-gray-700">Date:</span>
                <div className="mt-2 border-b border-gray-400 pb-1">
                  <span className="text-sm text-gray-900">{currentDate}</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-700">Signature of Depositor</span>
                <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[200px]">
                  <span className="text-sm text-gray-400">________________________</span>
                </div>
              </div>
            </div>

            {/* Witnesses Section */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-800 mb-4">Two Witnesses to Signature</p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <span className="text-sm text-gray-700">1.</span>
                  <div className="mt-2 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                  <div className="mt-4 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-700">2.</span>
                  <div className="mt-2 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                  <div className="mt-4 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chief Executive Officer Section */}
            <div className="text-right mt-12">
              <p className="text-sm text-gray-700">Chief Executive Officer</p>
              <p className="text-sm text-gray-700 mt-1">Pune Cantonment Board, Pune.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
